/**
 * 🎯 OverlayScrollbar - A Custom Scrollbar Component Inspired by Slack's Brilliant Design
 * 
 * This component implements a custom overlay scrollbar that hides the native browser scrollbar
 * while maintaining all native scrolling behaviors (keyboard navigation, mouse wheel, smooth scrolling).
 * 
 * 🚀 Key Features:
 * - Hides native scrollbar across all browsers (Chrome, Firefox, Safari, Edge)
 * - Maintains all native scroll behaviors (keyboard, mouse wheel, smooth scrolling)
 * - Shows/hides on hover (configurable)
 * - Works with any parent positioning context (sticky, absolute, relative, etc.)
 * - Responsive to content changes and container resizing
 * - Zero boilerplate required - just wrap your content!
 * 
 * 📚 Inspired by: https://www.bennadel.com/blog/3864-copying-slacks-brilliant-virtual-scrollbar-and-overflow-container-in-angular-9-1-12.htm
 * 
 * 💡 Usage Examples:
 * 
 * // Basic usage - works anywhere!
 * <OverlayScrollbar>
 *   <div>Your scrollable content here...</div>
 * </OverlayScrollbar>
 * 
 * // In a VizShell panel
 * <VizPanel>
 *   <OverlayScrollbar>
 *     <Legend data={legendData} />
 *   </OverlayScrollbar>
 * </VizPanel>
 * 
 * // Always visible scrollbar
 * <OverlayScrollbar alwaysVisible={true}>
 *   <div>Content that needs persistent scrollbar</div>
 * </OverlayScrollbar>
 * 
 * // Custom styling
 * <OverlayScrollbar className="my-custom-scrollbar">
 *   <div>Styled content</div>
 * </OverlayScrollbar>
 * 
 * // Side-positioned scrollbar (takes up space like normal scrollbar)
 * <OverlayScrollbar overlay={false}>
 *   <div>Content with side scrollbar</div>
 * </OverlayScrollbar>
 * 
 * ⚠️ Important Notes:
 * - Only renders scrollbar when content actually overflows
 * - Automatically handles content changes and container resizing
 * - Works with any container size (no minimum width requirements)
 * - Compatible with all modern browsers
 * 
 * 🎨 Styling:
 * The scrollbar uses colors2.grey15/grey20/grey30 for theming.
 * You can override styles by targeting the component's className.
 */

import { colors2 } from 'oolib';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

// ============================================================================
// 🎨 STYLED COMPONENTS - The Visual Foundation
// ============================================================================

/**
 * 📦 Container - The main wrapper that establishes the layout context
 * 
 * Key Design Decisions:
 * - NO position: relative - This allows it to inherit positioning from parent
 *   (crucial for working with sticky, absolute, etc. parent contexts)
 * - display: flex + flex-direction: column - Creates a flexible layout
 * - overflow: hidden - Clips content and hides native scrollbars
 */
const Container = styled.div`
  /* NO position: relative - let it inherit positioning context */
  overflow: hidden;
  height: inherit;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

/**
 * 🖼️ ViewportWrapper - Creates positioning context for the virtual scrollbar
 * 
 * This is the ONLY element with position: relative in our component.
 * It provides the positioning context for the absolutely positioned virtual scrollbar.
 * When overlay is false, it uses flex layout to position scrollbars on the side.
 */
const ViewportWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  ${props => !props.overlay && `
    display: flex;
    flex-direction: column;
  `}
`;

/**
 * 📦 InnerWrapper - Wraps viewport and vertical scrollbar when overlay is false
 * 
 * This creates a horizontal flex container for viewport + vertical scrollbar
 * when scrollbars are positioned on the side.
 */
const InnerWrapper = styled.div`
  ${props => !props.overlay ? `
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow: hidden;
  ` : `
    height: 100%;
  `}
`;

/**
 * 📜 Viewport - The actual scrollable area with hidden native scrollbar
 * 
 * This is where the magic happens! We hide the native scrollbar using CSS
 * while keeping all the native scroll behaviors intact.
 * 
 * Cross-browser scrollbar hiding:
 * - WebKit (Chrome, Safari): &::-webkit-scrollbar { width: 0px; }
 * - Firefox: scrollbar-width: none;
 * - IE/Edge: -ms-overflow-style: none;
 */
const Viewport = styled.div`
  height: 100%;
  overflow-x: auto;
  overflow-y: auto;
  ${props => !props.overlay && `
    flex: 1;
  `}
  
  /* Hide scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  
  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
  
  /* Hide scrollbar for IE and Edge */
  -ms-overflow-style: none;
`;

/**
 * 📄 Content - Wrapper for user content
 * 
 * Simple wrapper that ensures proper box-sizing and width.
 */
const Content = styled.div`
  /* Content wrapper */
  width: 100%;
  box-sizing: border-box;
`;

/**
 * 🎯 VirtualScrollbar - Our custom scrollbar overlay
 * 
 * This is positioned absolutely over the viewport (when overlay=true) or in normal flow
 * (when overlay=false) and provides the visual scrollbar that users interact with.
 * It's only rendered when there's overflow.
 */
const VirtualScrollbar = styled.div`
  ${props => props.overlay ? `
    position: absolute;
    z-index: 1000;
  ` : `
    position: relative;
    flex-shrink: 0;
  `}
  ${props => props.orientation === 'horizontal' ? `
    ${props.overlay ? `
      left: 0;
      right: 0;
      bottom: 0;
    ` : `
      width: 100%;
    `}
    height: 1rem;
  ` : `
    ${props.overlay ? `
      right: 0;
      top: 0;
      bottom: 0;
    ` : `
      height: 100%;
    `}
    width: 1rem;
  `}
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.15s ease;
  user-select: none;
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

/**
 * 🛤️ ScrollbarTrack - The clickable track area
 * 
 * Users can click anywhere on the track to jump to that position.
 * It's transparent but captures click events.
 */
const ScrollbarTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  cursor: pointer;
`;

/**
 * 🎚️ ScrollbarThumb - The draggable scrollbar handle
 * 
 * This is what users actually see and interact with. It changes color on hover
 * and when dragging to provide visual feedback.
 */
const ScrollbarThumb = styled.div`
  position: absolute;
  ${props => props.orientation === 'horizontal' ? `
    top: 2px;
    bottom: 2px;
    min-width: 20px;
  ` : `
  left: 2px;
  right: 2px;
    min-height: 20px;
  `}
  background: ${colors2.grey15};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${colors2.grey20};
  }
  
  ${props => props.isDragging && `
    background: ${colors2.grey30} !important;
  `}
`;

// ============================================================================
// 🎯 MAIN COMPONENT - The Brain Behind the Magic
// ============================================================================

/**
 * OverlayScrollbar Component
 * 
 * Props:
 * @param {React.ReactNode} children - Content to be made scrollable
 * @param {string} className - Custom CSS class for styling
 * @param {boolean} showOnHover - Show scrollbar only on hover (default: true)
 * @param {boolean} alwaysVisible - Keep scrollbar always visible (default: false)
 * @param {boolean} enableHorizontal - Enable horizontal scrollbar (default: false)
 * @param {boolean} enableVertical - Enable vertical scrollbar (default: true)
 * @param {number} thumbMinHeight - Minimum height for scrollbar thumb (default: 10)
 * @param {number} thumbMinWidth - Minimum width for horizontal scrollbar thumb (default: 10)
 * @param {boolean} overlay - Position scrollbar on top of container (overlay) or on the side taking up space (default: true)
 * @param {...object} props - Additional props passed to Container
 * 
 * 
 * 1. ✅ Conditional rendering - Only shows scrollbar when content overflows
 * 2. ✅ Cross-browser scrollbar hiding - Works on all major browsers
 * 3. ✅ Positioning context independence - Works with any parent positioning
 * 4. ✅ Dynamic content handling - Responds to content changes and resizing
 * 5. ✅ Initial render fix - Shows scrollbar immediately when needed
 * 6. ✅ Horizontal scrolling support - Can show both vertical and horizontal scrollbars
 * 7. ✅ Overlay/Side positioning - Can position scrollbar on top or on the side
 */
const OverlayScrollbar = ({ 
  children, 
  className,
  showOnHover = true,
  alwaysVisible = false,
  enableHorizontal = false,
  enableVertical = true,
  thumbMinHeight = 10,
  thumbMinWidth = 10,
  overlay = true,
  ...props 
}) => {
  const containerRef = useRef(null);
  const viewportWrapperRef = useRef(null);
  const viewportRef = useRef(null);
  const verticalScrollbarRef = useRef(null);
  const verticalThumbRef = useRef(null);
  const horizontalScrollbarRef = useRef(null);
  const horizontalThumbRef = useRef(null);
  
  const [verticalScrollbarVisible, setVerticalScrollbarVisible] = useState(alwaysVisible);
  const [horizontalScrollbarVisible, setHorizontalScrollbarVisible] = useState(alwaysVisible);
  const [isDraggingVertical, setIsDraggingVertical] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [verticalScrollState, setVerticalScrollState] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    scrollPercentage: 0
  });
  const [horizontalScrollState, setHorizontalScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
    scrollPercentage: 0
  });

  // ============================================================================
  // 🔧 UTILITY FUNCTIONS - The Helper Functions
  // ============================================================================

  /**
   * 📏 Measure Native Scrollbar Width
   * 
   * This function dynamically measures the native scrollbar width on the user's system.
   * This is crucial for proper positioning and cross-platform compatibility.
   * 
   * Edge Cases Handled:
   * - Different OS scrollbar widths (Windows: ~17px, macOS: ~15px, Linux: varies)
   * - High DPI displays that might have different scrollbar sizes
   * - Minimum width fallback for better UX
   */
  const measureScrollbarWidth = useCallback(() => {
    // Create a temporary element to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    outer.style.msOverflowStyle = 'scrollbar'; // Needed for WinJS apps
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return Math.max(scrollbarWidth, 15); // Minimum 15px for better UX
  }, []);

  // Update scroll state
  const updateScrollState = useCallback(() => {
    if (!viewportRef.current) return;

    const viewport = viewportRef.current;
    
    // Vertical scroll state
    if (enableVertical) {
    const scrollTop = viewport.scrollTop;
    const scrollHeight = viewport.scrollHeight;
    const clientHeight = viewport.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    const scrollPercentage = maxScroll > 0 ? scrollTop / maxScroll : 0;

      setVerticalScrollState({
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollPercentage
    });
    }
    
    // Horizontal scroll state
    if (enableHorizontal) {
      const scrollLeft = viewport.scrollLeft;
      const scrollWidth = viewport.scrollWidth;
      const clientWidth = viewport.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const scrollPercentage = maxScroll > 0 ? scrollLeft / maxScroll : 0;

      setHorizontalScrollState({
        scrollLeft,
        scrollWidth,
        clientWidth,
        scrollPercentage
      });
    }
  }, [enableVertical, enableHorizontal]);

  // Handle viewport scroll
  const handleViewportScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  // Handle mouse enter/leave for hover behavior
  const handleMouseEnter = useCallback(() => {
    if (showOnHover) {
      if (enableVertical && verticalScrollState.scrollHeight > verticalScrollState.clientHeight) {
        setVerticalScrollbarVisible(true);
      }
      if (enableHorizontal && horizontalScrollState.scrollWidth > horizontalScrollState.clientWidth) {
        setHorizontalScrollbarVisible(true);
      }
    }
  }, [showOnHover, enableVertical, enableHorizontal, verticalScrollState.scrollHeight, verticalScrollState.clientHeight, horizontalScrollState.scrollWidth, horizontalScrollState.clientWidth]);

  const handleMouseLeave = useCallback(() => {
    if (showOnHover) {
      if (!isDraggingVertical) {
        setVerticalScrollbarVisible(false);
      }
      if (!isDraggingHorizontal) {
        setHorizontalScrollbarVisible(false);
      }
    }
  }, [showOnHover, isDraggingVertical, isDraggingHorizontal]);

  // Handle vertical scrollbar track click
  const handleVerticalTrackClick = useCallback((e) => {
    if (!viewportRef.current || !verticalScrollbarRef.current) return;

    const scrollbarRect = verticalScrollbarRef.current.getBoundingClientRect();
    const clickY = e.clientY - scrollbarRect.top;
    const scrollbarHeight = scrollbarRect.height;
    const targetPercentage = clickY / scrollbarHeight;
    
    const viewport = viewportRef.current;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;
    viewport.scrollTop = targetPercentage * maxScroll;
  }, []);

  // Handle horizontal scrollbar track click
  const handleHorizontalTrackClick = useCallback((e) => {
    if (!viewportRef.current || !horizontalScrollbarRef.current) return;

    const scrollbarRect = horizontalScrollbarRef.current.getBoundingClientRect();
    const clickX = e.clientX - scrollbarRect.left;
    const scrollbarWidth = scrollbarRect.width;
    const targetPercentage = clickX / scrollbarWidth;
    
    const viewport = viewportRef.current;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    viewport.scrollLeft = targetPercentage * maxScroll;
  }, []);

  // Handle vertical thumb drag
  const handleVerticalThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!viewportRef.current || !verticalScrollbarRef.current || !verticalThumbRef.current) return;

    setIsDraggingVertical(true);
    
    const viewport = viewportRef.current;
    const scrollbar = verticalScrollbarRef.current;
    const startY = e.clientY;
    const startScrollTop = viewport.scrollTop;
    const scrollbarHeight = scrollbar.clientHeight;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;

    const handleMouseMove = (e) => {
      const deltaY = e.clientY - startY;
      const scrollDelta = (deltaY / scrollbarHeight) * maxScroll;
      viewport.scrollTop = Math.max(0, Math.min(maxScroll, startScrollTop + scrollDelta));
    };

    const handleMouseUp = () => {
      setIsDraggingVertical(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (showOnHover) {
        setVerticalScrollbarVisible(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [showOnHover]);

  // Handle horizontal thumb drag
  const handleHorizontalThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!viewportRef.current || !horizontalScrollbarRef.current || !horizontalThumbRef.current) return;

    setIsDraggingHorizontal(true);
    
    const viewport = viewportRef.current;
    const scrollbar = horizontalScrollbarRef.current;
    const startX = e.clientX;
    const startScrollLeft = viewport.scrollLeft;
    const scrollbarWidth = scrollbar.clientWidth;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const scrollDelta = (deltaX / scrollbarWidth) * maxScroll;
      viewport.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + scrollDelta));
    };

    const handleMouseUp = () => {
      setIsDraggingHorizontal(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (showOnHover) {
        setHorizontalScrollbarVisible(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [showOnHover]);

  // Calculate vertical thumb position and height
  const getVerticalThumbStyle = useCallback(() => {
    if (!verticalScrollbarRef.current) return {};

    const scrollbarHeight = verticalScrollbarRef.current.clientHeight;
    const { scrollHeight, clientHeight, scrollPercentage } = verticalScrollState;
    
    if (scrollHeight <= clientHeight) return { display: 'none' };

    const thumbHeight = Math.max(
      thumbMinHeight,
      (clientHeight / scrollHeight) * scrollbarHeight
    );
    
    const maxThumbTop = scrollbarHeight - thumbHeight;
    const thumbTop = scrollPercentage * maxThumbTop;

    return {
      top: `${thumbTop}px`,
      height: `${thumbHeight}px`
    };
  }, [verticalScrollState, thumbMinHeight]);

  // Calculate horizontal thumb position and width
  const getHorizontalThumbStyle = useCallback(() => {
    if (!horizontalScrollbarRef.current) return {};

    const scrollbarWidth = horizontalScrollbarRef.current.clientWidth;
    const { scrollWidth, clientWidth, scrollPercentage } = horizontalScrollState;
    
    if (scrollWidth <= clientWidth) return { display: 'none' };

    const thumbWidth = Math.max(
      thumbMinWidth,
      (clientWidth / scrollWidth) * scrollbarWidth
    );
    
    const maxThumbLeft = scrollbarWidth - thumbWidth;
    const thumbLeft = scrollPercentage * maxThumbLeft;

    return {
      left: `${thumbLeft}px`,
      width: `${thumbWidth}px`
    };
  }, [horizontalScrollState, thumbMinWidth]);

  // ============================================================================
  // 🎬 EFFECTS - The Lifecycle Management
  // ============================================================================

  /**
   * 🚀 Initialize Scroll State
   * 
   * This effect handles the initial setup and ensures the scrollbar appears
   * immediately when content overflows (fixes the "hover required" issue).
   * 
   * Key Fix: Double updateScrollState() call ensures DOM is fully rendered
   * before calculating scroll dimensions.
   */
  useEffect(() => {
    const measuredWidth = measureScrollbarWidth();
    setScrollbarWidth(measuredWidth);
    
    // Update scroll state immediately and after a small delay to ensure DOM is ready
    updateScrollState();
    const timeoutId = setTimeout(() => {
      updateScrollState();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [updateScrollState, measureScrollbarWidth]);

  /**
   * 📡 Set Up Viewport Scroll Listener
   * 
   * Listens to native scroll events and updates our virtual scrollbar position.
   * Uses passive: true for better performance.
   */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.addEventListener('scroll', handleViewportScroll, { passive: true });
    
    return () => {
      viewport.removeEventListener('scroll', handleViewportScroll);
    };
  }, [handleViewportScroll]);

  /**
   * 👁️ Handle Always Visible Mode
   * 
   * When alwaysVisible is true, the scrollbar stays visible regardless of hover state.
   */
  useEffect(() => {
    if (alwaysVisible) {
      setVerticalScrollbarVisible(true);
      setHorizontalScrollbarVisible(true);
    }
  }, [alwaysVisible]);

  /**
   * 📏 Handle Container Resize
   * 
   * Uses ResizeObserver to detect when the container size changes and updates
   * scroll state accordingly. This ensures the scrollbar works correctly in
   * responsive layouts.
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Update scroll state when container resizes
      setTimeout(() => updateScrollState(), 0);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  /**
   * 🔄 Update Scroll State When Children Change
   * 
   * This effect ensures the scrollbar appears/disappears when content changes.
   * Critical for dynamic content scenarios (e.g., switching tabs, loading data).
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateScrollState();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [children, updateScrollState]);

  // ============================================================================
  // 🎨 RENDER - The Final Output
  // ============================================================================

  const verticalThumbStyle = getVerticalThumbStyle();
  const horizontalThumbStyle = getHorizontalThumbStyle();
  const hasVerticalOverflow = verticalScrollState.scrollHeight > verticalScrollState.clientHeight;
  const hasHorizontalOverflow = horizontalScrollState.scrollWidth > horizontalScrollState.clientWidth;
  const showVerticalScrollbar = verticalScrollbarVisible && hasVerticalOverflow && enableVertical;
  const showHorizontalScrollbar = horizontalScrollbarVisible && hasHorizontalOverflow && enableHorizontal;

  /**
   * 🎯 Key Fix: Conditional Rendering
   * 
   * The VirtualScrollbars are only rendered when there's overflow in their respective direction.
   * This prevents scrollbars from showing when content fits within the container.
   * 
   * Layout structure:
   * - When overlay=true: Scrollbars are absolutely positioned over the viewport
   * - When overlay=false: Scrollbars are in normal flow, taking up space
   *   - Vertical scrollbar is on the right side of the viewport
   *   - Horizontal scrollbar is at the bottom
   */
  return (
    <Container
      ref={containerRef}
      className={"overlayScrollbarContainer"}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <ViewportWrapper 
        ref={viewportWrapperRef}
        overlay={overlay}
      >
        <InnerWrapper overlay={overlay}>
          <Viewport ref={viewportRef} overlay={overlay}>
            <Content>
              {children}
            </Content>
          </Viewport>
          
          {/* Vertical Scrollbar */}
          {hasVerticalOverflow && enableVertical && (
            <VirtualScrollbar
              ref={verticalScrollbarRef}
              visible={showVerticalScrollbar}
              orientation="vertical"
              overlay={overlay}
            >
              <ScrollbarTrack onClick={handleVerticalTrackClick} />
              <ScrollbarThumb
                ref={verticalThumbRef}
                style={verticalThumbStyle}
                isDragging={isDraggingVertical}
                onMouseDown={handleVerticalThumbMouseDown}
                orientation="vertical"
              />
            </VirtualScrollbar>
          )}
        </InnerWrapper>
        
        {/* Horizontal Scrollbar */}
        {hasHorizontalOverflow && enableHorizontal && (
          <VirtualScrollbar
            ref={horizontalScrollbarRef}
            visible={showHorizontalScrollbar}
            orientation="horizontal"
            overlay={overlay}
          >
            <ScrollbarTrack onClick={handleHorizontalTrackClick} />
            <ScrollbarThumb
              ref={horizontalThumbRef}
              style={horizontalThumbStyle}
              isDragging={isDraggingHorizontal}
              onMouseDown={handleHorizontalThumbMouseDown}
              orientation="horizontal"
            />
          </VirtualScrollbar>
        )}
      </ViewportWrapper>
    </Container>
  );
};

// ============================================================================
// 🚨 EDGE CASES & LIMITATIONS - What You Should Know
// ============================================================================

/**
 * ⚠️ Known Limitations:
 * 
 * 1. **Nested Scrollbars**: If child components have their own overflow: auto,
 *    they might conflict with this component. Use disableInternalScroll prop
 *    on child components when possible.
 * 
 * 2. **Very Small Containers**: On extremely small containers (< 50px width/height),
 *    the scrollbar might not render properly due to minimum thumb size.
 * 
 * 3. **Touch Devices**: While it works on touch devices, the native scrollbar
 *    behavior might be more intuitive for touch users.
 * 
 * 4. **Browser Compatibility**: Requires modern browsers that support:
 *    - CSS custom properties (CSS variables)
 *    - ResizeObserver API
 *    - Modern scrollbar hiding CSS properties
 * 
 * 🎯 Best Practices:
 * 
 * - Always wrap content that might overflow
 * - Use alwaysVisible={true} for critical scrollable areas
 * - Use enableHorizontal={true} when content can overflow horizontally
 * - Test on different screen sizes and orientations
 * - Consider accessibility - some users prefer native scrollbars
 * 
 * 🔧 Troubleshooting:
 * 
 * - If scrollbar doesn't appear: Check if content actually overflows and enableVertical/enableHorizontal are set
 * - If native scrollbar shows: Ensure child components don't have overflow: auto
 * - If positioning is off: Check parent container's positioning context
 * - If scrollbar flickers: Content might be changing size dynamically
 */

export default OverlayScrollbar;
